// src/pages/SignIn.jsx
// Wayroot — Enterprise OTP Sign-in (single clean card, no marketing/pitch panels)

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useUser } from "../context/UserContext";

function cleanPhone(p = "") {
  return String(p).replace(/\D/g, "").slice(-10);
}

function maskPhone10(p = "") {
  const s = cleanPhone(p);
  if (s.length !== 10) return s;
  return `+91 ${s.slice(0, 2)}••• •••${s.slice(7)}`;
}

export default function SignIn() {
  const { userRequestOtp, userSignInOtp } = useUser();
  const nav = useNavigate();
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const redirect = params.get("redirect") || "/user-dashboard";

  const [step, setStep] = useState("phone"); // "phone" | "otp"
  const [phoneNumber, setPhoneNumber] = useState("");
  const normalizedPhone = useMemo(() => cleanPhone(phoneNumber), [phoneNumber]);

  const [otp, setOtp] = useState("");

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // Resend cooldown (keeps it professional & prevents spam)
  const [cooldown, setCooldown] = useState(0);
  const timerRef = useRef(null);

  const canSendOtp = normalizedPhone.length === 10 && !busy && cooldown === 0;
  const canVerifyOtp =
    step === "otp" && normalizedPhone.length === 10 && otp.trim().length >= 4 && !busy;

  useEffect(() => {
    if (cooldown <= 0) return;
    timerRef.current = window.setInterval(() => {
      setCooldown((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
      timerRef.current = null;
    };
  }, [cooldown]);

  const startCooldown = (seconds = 30) => setCooldown(seconds);

  const resetToPhone = () => {
    setError("");
    setOtp("");
    setStep("phone");
  };

  const handleSendOtp = async () => {
    setError("");
    if (normalizedPhone.length !== 10) {
      setError("Enter a valid 10-digit mobile number.");
      return;
    }
    try {
      setBusy(true);
      await userRequestOtp(normalizedPhone);
      setStep("otp");
      setOtp("");
      startCooldown(30);
    } catch {
      setError("Could not send OTP. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError("");

    if (normalizedPhone.length !== 10) {
      setError("Phone number looks invalid. Please re-enter.");
      resetToPhone();
      return;
    }
    if (otp.trim().length < 4) {
      setError("Enter OTP and try again.");
      return;
    }

    try {
      setBusy(true);
      const res = await userSignInOtp({ phoneNumber: normalizedPhone, otp: otp.trim() });

      if (res?.isNewUser) {
        nav(
          `/signup?redirect=${encodeURIComponent(redirect)}&via=signin&phoneNumber=${encodeURIComponent(
            normalizedPhone
          )}`,
          { replace: true }
        );
        return;
      }
      nav(redirect, { replace: true });
    } catch {
      setError("Invalid or expired OTP. Please request a new OTP.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <Header />

      <main className="mx-auto w-full max-w-[560px] px-4 sm:px-6 xl:px-8 py-10 sm:py-14">
        <div className="rounded-3xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          {/* subtle top accent */}
          <div className="h-1 w-full bg-gradient-to-r from-black via-[#FF2D83] to-black" />

          <div className="p-6 sm:p-8">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h1 className="text-2xl font-bold tracking-tight">Sign in</h1>
                <p className="mt-1 text-sm text-slate-600">
                  {step === "phone"
                    ? "Enter your mobile number to receive a one-time password."
                    : `Enter the OTP sent to ${maskPhone10(normalizedPhone)}.`}
                </p>
              </div>

              {/* step indicator */}
              <div className="flex items-center gap-2 pt-1">
                <StepDot active />
                <div className={`h-[2px] w-10 ${step === "otp" ? "bg-slate-900" : "bg-slate-200"}`} />
                <StepDot active={step === "otp"} />
              </div>
            </div>

            {error && (
              <div className="mt-5 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {error}
              </div>
            )}

            {step === "phone" ? (
              <div className="mt-6 space-y-5">
                <label className="block">
                  <span className="text-sm font-medium text-slate-700">Mobile number</span>

                  <div className="mt-2 flex items-center gap-3 rounded-2xl border border-slate-300 bg-white px-3.5 py-2.5 focus-within:ring-2 focus-within:ring-slate-900/10">
                    <span className="text-sm font-semibold text-slate-700 select-none">+91</span>
                    <div className="h-5 w-px bg-slate-200" />
                    <input
                      type="tel"
                      inputMode="numeric"
                      autoComplete="tel"
                      placeholder="10-digit number"
                      value={phoneNumber}
                      onChange={(e) => {
                        setPhoneNumber(cleanPhone(e.target.value));
                        setError("");
                      }}
                      className="w-full bg-transparent text-[15px] outline-none placeholder:text-slate-400"
                    />
                  </div>

                  <div className="mt-2 text-xs text-slate-500">
                    We’ll send a verification code to this number.
                  </div>
                </label>

                <button
                  type="button"
                  onClick={handleSendOtp}
                  disabled={!canSendOtp}
                  className="w-full inline-flex items-center justify-center rounded-2xl bg-black px-5 py-3 text-white font-medium hover:opacity-90 active:opacity-80 disabled:opacity-60"
                >
                  {busy ? "Sending OTP..." : "Send OTP"}
                </button>

                <div className="pt-2 text-sm text-slate-700">
                  New to Wayroot?{" "}
                  <Link
                    to={`/signup?redirect=${encodeURIComponent(redirect)}`}
                    className="font-semibold underline underline-offset-4 hover:no-underline"
                  >
                    Create an account
                  </Link>
                </div>
              </div>
            ) : (
              <form onSubmit={handleVerifyOtp} className="mt-6 space-y-5">
                <div className="flex items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                  <div>
                    <div className="text-[11px] font-semibold tracking-[0.18em] text-slate-500">
                      MOBILE
                    </div>
                    <div className="mt-0.5 text-sm font-semibold text-slate-900">
                      {maskPhone10(normalizedPhone)}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={resetToPhone}
                    className="text-xs font-semibold text-[#FF2D83] hover:opacity-80"
                  >
                    Change
                  </button>
                </div>

                <label className="block">
                  <span className="text-sm font-medium text-slate-700">One-time password</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder="4–6 digit OTP"
                    value={otp}
                    onChange={(e) => {
                      setOtp(e.target.value.replace(/\D/g, "").slice(0, 6));
                      setError("");
                    }}
                    className="mt-2 w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-[16px] tracking-[0.22em] text-slate-900 outline-none focus:ring-2 focus:ring-slate-900/10 placeholder:tracking-normal placeholder:text-slate-400"
                  />

                  <div className="mt-2 flex items-center justify-between text-xs text-slate-500">
                    <span>Didn’t receive it?</span>
                    <button
                      type="button"
                      onClick={handleSendOtp}
                      disabled={busy || normalizedPhone.length !== 10 || cooldown > 0}
                      className="font-semibold text-[#FF2D83] disabled:text-slate-300"
                    >
                      {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend OTP"}
                    </button>
                  </div>
                </label>

                <button
                  type="submit"
                  disabled={!canVerifyOtp}
                  className="w-full inline-flex items-center justify-center rounded-2xl bg-black px-5 py-3 text-white font-medium hover:opacity-90 active:opacity-80 disabled:opacity-60"
                >
                  {busy ? "Verifying..." : "Verify & continue"}
                </button>

                <div className="text-xs text-slate-500 leading-5">
                  For security, OTP is time-bound and may require a resend.
                </div>
              </form>
            )}
          </div>
        </div>

        <div className="mt-6 text-center text-xs text-slate-500">
          Having trouble? Confirm SMS access and try again.
        </div>
      </main>

      <Footer />
    </div>
  );
}

function StepDot({ active = false }) {
  return (
    <div
      className={[
        "h-2.5 w-2.5 rounded-full border",
        active ? "bg-slate-900 border-slate-900" : "bg-white border-slate-300",
      ].join(" ")}
    />
  );
}
